
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Clipboard, ArrowLeft, RefreshCw, Edit, Save, X } from "lucide-react";
import { SubscriptionTiers } from "@/components/subscription/SubscriptionTiers";

interface GeneratedMessage {
  id: string;
  client_id: string;
  message_type: 'email' | 'text';
  content: string;
  created_at: string;
}

const GeneratedContent = () => {
  const [messages, setMessages] = useState<GeneratedMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [editMode, setEditMode] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const navigate = useNavigate();
  const {
    toast
  } = useToast();

  useEffect(() => {
    const fetchMessages = async () => {
      setIsLoading(true);
      try {
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();
        if (session) {
          setIsAuthenticated(true);
          const {
            data,
            error
          } = await supabase.from('generated_messages').select('*').order('created_at', {
            ascending: false
          }).limit(2);
          if (error) throw error;
          if (data && data.length > 0) {
            setMessages(data as GeneratedMessage[]);
          }
        } else {
          const storedMessages = localStorage.getItem('generatedMessages');
          if (storedMessages) {
            setMessages(JSON.parse(storedMessages));
          } else {
            navigate('/client-info');
            return;
          }
        }
      } catch (error: any) {
        console.error('Error fetching messages:', error);
        toast({
          title: "Error",
          description: "Failed to load messages. Please try again.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchMessages();
  }, [navigate, toast]);

  const copyToClipboard = (content: string) => {
    navigator.clipboard.writeText(content);
    setIsCopied(true);
    toast({
      title: "Copied!",
      description: "Message copied to clipboard."
    });
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  const handleBackClick = () => {
    navigate('/client-info');
  };

  const handleEdit = (messageId: string, content: string) => {
    setEditMode(messageId);
    setEditContent(content);
  };

  const handleCancelEdit = () => {
    setEditMode(null);
    setEditContent("");
  };

  const handleSaveEdit = async (messageId: string, messageType: 'email' | 'text') => {
    try {
      if (isAuthenticated) {
        const {
          error
        } = await supabase.from('generated_messages').update({
          content: editContent
        }).eq('id', messageId);
        if (error) throw error;
      } else {
        const updatedMessages = messages.map(msg => msg.id === messageId ? {
          ...msg,
          content: editContent
        } : msg);
        setMessages(updatedMessages);
        localStorage.setItem('generatedMessages', JSON.stringify(updatedMessages));
      }
      toast({
        title: "Saved!",
        description: `Your ${messageType} message has been updated.`
      });
      setEditMode(null);
    } catch (error: any) {
      console.error('Error saving edit:', error);
      toast({
        title: "Error",
        description: "Failed to save your changes. Please try again.",
        variant: "destructive"
      });
    }
  };

  const emailMessage = messages.find(msg => msg.message_type === 'email');
  const textMessage = messages.find(msg => msg.message_type === 'text');

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-2">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-primary">Loading your messages...</p>
        </div>
      </div>;
  }

  if (messages.length === 0) {
    return <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <h2 className="text-xl font-bold">No Messages Found</h2>
              <p className="text-gray-500">You haven't generated any messages yet.</p>
              <Button onClick={handleBackClick}>Generate Messages</Button>
            </div>
          </CardContent>
        </Card>
      </div>;
  }

  return <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" className="gap-2" onClick={handleBackClick}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          
          {isAuthenticated && <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>}
        </div>

        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Keep in Touch, Effortlessly—Here's Your Message</h1>
          <p className="text-gray-500">
            Copy and use these messages to reconnect with your client.
          </p>
        </div>

        <Tabs defaultValue="email" className="w-full">
          <TabsList className="grid grid-cols-2 mb-4">
            <TabsTrigger value="email">Email</TabsTrigger>
            <TabsTrigger value="text">Text Message</TabsTrigger>
          </TabsList>
          
          <TabsContent value="email">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Email Message</span>
                  <div className="flex space-x-2">
                    {editMode === emailMessage?.id ? <>
                        <Button variant="outline" size="sm" onClick={handleCancelEdit} className="gap-2">
                          <X className="h-4 w-4" />
                          Cancel
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => emailMessage && handleSaveEdit(emailMessage.id, 'email')} className="gap-2">
                          <Save className="h-4 w-4" />
                          Save
                        </Button>
                      </> : <>
                        <Button variant="outline" size="sm" onClick={() => emailMessage && handleEdit(emailMessage.id, emailMessage.content)} className="gap-2">
                          <Edit className="h-4 w-4" />
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => emailMessage && copyToClipboard(emailMessage.content)} className="gap-2">
                          <Clipboard className="h-4 w-4" />
                          Copy
                        </Button>
                      </>}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {editMode === emailMessage?.id ? <Textarea value={editContent} onChange={e => setEditContent(e.target.value)} className="min-h-[200px] leading-relaxed" /> : <div className="whitespace-pre-wrap border rounded-md p-4 bg-gray-50">
                    {emailMessage?.content}
                  </div>}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="text">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Text Message</span>
                  <div className="flex space-x-2">
                    {editMode === textMessage?.id ? <>
                        <Button variant="outline" size="sm" onClick={handleCancelEdit} className="gap-2">
                          <X className="h-4 w-4" />
                          Cancel
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => textMessage && handleSaveEdit(textMessage.id, 'text')} className="gap-2">
                          <Save className="h-4 w-4" />
                          Save
                        </Button>
                      </> : <>
                        <Button variant="outline" size="sm" onClick={() => textMessage && handleEdit(textMessage.id, textMessage.content)} className="gap-2">
                          <Edit className="h-4 w-4" />
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => textMessage && copyToClipboard(textMessage.content)} className="gap-2">
                          <Clipboard className="h-4 w-4" />
                          Copy
                        </Button>
                      </>}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {editMode === textMessage?.id ? <Textarea value={editContent} onChange={e => setEditContent(e.target.value)} className="min-h-[150px] leading-relaxed" /> : <div className="whitespace-pre-wrap border rounded-md p-4 bg-gray-50">
                    {textMessage?.content}
                  </div>}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {!isAuthenticated && <div className="space-y-6">
            <div className="text-center mb-4">
              <Button 
                variant="secondary" 
                className="mx-auto" 
                onClick={() => {
                  if (messages.length > 0) {
                    localStorage.setItem('generatedMessages', JSON.stringify(messages));
                  }
                  navigate("/auth?mode=signup");
                }}
              >
                Skip subscription and create account
              </Button>
            </div>
            
            <div className="py-4">
              <h2 className="text-2xl font-bold text-center mb-8">Ready for more?</h2>
              <SubscriptionTiers onSubscriptionSelected={() => navigate("/auth")} />
            </div>
          </div>}
      </div>
    </div>;
};

export default GeneratedContent;
